<script setup>
import { computed, onMounted } from 'vue'
import { useCalendarSettings } from '../composables/useCalendarSettings'
import { useGetAgeLimitString } from '~/core/composables/useGetAgeLimitString'

const categoriesOntology = ontologies.category?.plural
const hasCustomCategoriesOntology = categoriesOntology && categoriesOntology !== 'categories'

const { t } = useI18n()
const formModelValue = defineModel()

const { isWidget, widgetSettingsOptions } = useWidgetSettings()
const { isPeloton } = useEnterpriseClient()
const { show_offering_type_filter, show_venue_filter, show_category_filter, show_age_restriction_filter: showAgeFilter } = useCalendarSettings()
const { features } = useFeatures()
const { getAgeLimitString } = useGetAgeLimitString()

const formValue = ref(formModelValue.value)

const venueOptions = ref([])
const categoryOptions = ref([])
const instructorOptions = ref([])
const offeringTypeOptions = ref([])
const offerTypeDataResults = ref(null)

const api = useApi()

function sortByFirstAndLastName(aName, bName) {
  const aNameList = aName.split(' ')
  const bNameList = bName.split(' ')
  const aLastName = aNameList.at(-1)
  const bLastName = bNameList.at(-1)
  const byLastName = aLastName.localeCompare(bLastName)
  const aFirstName = aNameList[0]
  const bFirstName = bNameList[0]
  const byFirstName = aFirstName.localeCompare(bFirstName)
  return (byLastName * 2) + byFirstName
}

async function fetchVenueFilterOptions() {
  const venueData = await api.listVenues({
    queryParameters: {
      sort: 'name',
      has_active_sessions: true,
    },
  })

  const widgetVenueIds = isWidget && widgetSettingsOptions ? widgetSettingsOptions.venue_ids : []
  const hasWidgetVenueFilter = widgetVenueIds?.length

  venueOptions.value = venueData.results
    .map(venue => ({ value: venue.id.toString(), label: venue.name }))
    .filter(venue => !hasWidgetVenueFilter || widgetVenueIds.includes(+venue.value))
    .sort((a, b) => a.label.localeCompare(b.label))
}

async function fetchCategoryFilterOptions() {
  const categoryData = await api.listOfferingTypeCategories({ queryParameters: { sort: 'name' } })

  const getPelotonCategoryLabel = (categoryLabel) => {
    switch (categoryLabel) {
      case 'Rowing':
        return t('Rowing')
        // return computed(() => t('Rowing'))
      case 'Running/Walking':
        return t('Running/Walking')
        // return computed(() => t('Running/Walking'))
      case 'Strength/Cardio':
        return t('Strength/Cardio')
        // return computed(() => t('Strength/Cardio'))
      default:
        return categoryLabel
    }
  }

  categoryOptions.value = categoryData.results
    .map(category => ({
      value: category.id.toString(),
      label: isPeloton ? getPelotonCategoryLabel(category.name) : category.name,
      meta: { image: category.image?.url },
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

async function fetchInstructorFilterOptions() {
  const instructorData = await api.listInstructors({ queryParameters: { sort: 'name' } })
  instructorOptions.value = instructorData.results
    .map(instructor => ({ value: instructor.id.toString(), label: instructor.name, meta: { image: instructor.picture_url } }))
    .sort((a, b) => a.label.localeCompare(b.label))
    // .sort((a, b) => sortByFirstAndLastName(a.label, b.label))
}

const ageRestrictionOptions = computed(() => {
  const ageLimitOptions = offerTypeDataResults.value
    ?.filter(offeringType => offeringType.min_allowed_age || offeringType.max_allowed_age)
    .reduce((acc, offeringType) => {
      const min = offeringType.min_allowed_age
      const max = offeringType.max_allowed_age
      const key = `${min || ''}-${max || ''}`
      if (!acc[key]) {
        acc[key] = {
          min,
          max,
          offeringTypes: [offeringType.id.toString()],
        }
      }
      else {
        acc[key].offeringTypes.push(offeringType.id.toString())
      }
      return acc
    }, {})
  if (!ageLimitOptions)
    return []

  return Object.values(ageLimitOptions)
    .map((ageLimitOptions) => {
      return {
        value: JSON.stringify(ageLimitOptions.offeringTypes.join('|')),
        label: getAgeLimitString({ min: ageLimitOptions.min, max: ageLimitOptions.max }),
        meta: {
          min: ageLimitOptions.min,
          max: ageLimitOptions.max,
        },
      }
    })
    .sort((a, b) => {
      const minCompare = a.meta.min - b.meta.min
      const maxCompare = (a.meta.max && b.meta.max) ? a.meta.max - b.meta.max : a.meta.max ? -1 : 1
      return (minCompare * 2) + maxCompare
    })
})

async function fetchOfferingTypeFilterOptions() {
  const offeringTypeData = await api.listOfferingTypes({
    queryParameters: {
      schedule_type: ['class', 'course'],
      sort: 'name',
      has_active_sessions: true,
    },
  })
  offeringTypeOptions.value = offeringTypeData.results
    .map(offeringType => ({ value: offeringType.id.toString(), label: offeringType.name }))
    .sort((a, b) => a.label.localeCompare(b.label))

  if (features.age_restrictions && showAgeFilter) {
    offerTypeDataResults.value = offeringTypeData.results
  }
}

async function getFilterOptions() {
  if (show_venue_filter)
    fetchVenueFilterOptions()

  if (show_category_filter)
    fetchCategoryFilterOptions()

  if (show_offering_type_filter)
    fetchOfferingTypeFilterOptions()

  fetchInstructorFilterOptions()
}

onMounted(() => {
  getFilterOptions()
})

const hasFilters = computed(() => {
  return Object.keys(formValue.value).some(key =>
    Array.isArray(formValue.value[key]) && formValue.value[key].length > 0)
})

function clearFilters() {
  for (const key in formValue.value) {
    if (Array.isArray(formValue.value[key]))
      formValue.value[key].length = 0
  }
}

watch(
  formValue,
  val => formModelValue.value = val,
)
</script>

<template>
  <ScheduleFiltersLayout>
    <div class="h-full max-h-full w-270px overflow-scroll bg-surface-100 py-2 shadow-lg">
      <div class="flex flex-row items-center justify-between p-4">
        <div class="flex flex-row items-center flex-gap-x-2">
          <DSIcon
            icon="i-far-filter-list"
            size="24"
            class="text-secondary-300"
          />
          <span class="text-sm text-secondary-500">{{ t('Filter') }}</span>
        </div>
        <button
          v-if="hasFilters"
          class="m--2 flex flex-row items-center p-2"
          @click="clearFilters"
        >
          <DSIcon
            icon="i-far-xmark"
            size="24"
            class="text-secondary-300"
          />
          <span class="text-sm text-text-400">{{ t('Clear Filters') }}</span>
        </button>
      </div>
      <div>
        <FormKit
          v-model="formValue"
          type="form"
          :actions="false"
        >
          <ScheduleFiltersAccordion
            v-if="venueOptions.length > 1 && !(isWidget && widgetSettingsOptions?.disable_venue_selector)"
            :title="t('Venues')"
            name="venues"
            :options="venueOptions"
          />
          <ScheduleFiltersAccordion
            v-if="categoryOptions.length > 1"
            :title="isPeloton ? t('Fitness Disciplines') : t('Categories')"
            name="category"
            :options="categoryOptions"
          >
            <template #label="{ option }">
              <DSAvatar
                v-if="option?.meta?.image"
                :name="option.label"
                :image-url="option.meta.image"
                class="me-4"
              /> <span class="text-sm text-text-400">{{ option.label }}</span>
            </template>
          </ScheduleFiltersAccordion>
          <ScheduleFiltersAccordion
            v-if="instructorOptions.length > 1"
            :title="t('Instructors')"
            name="instructors"
            :options="instructorOptions"
          >
            <template #label="{ option }">
              <DSAvatar
                :name="option.label"
                :image-url="option.meta.image"
                class="me-4"
              /> <span class="text-sm text-text-400">{{ option.label }}</span>
            </template>
          </ScheduleFiltersAccordion>
          <ScheduleFiltersAccordion
            v-if="offeringTypeOptions.length > 1"
            :title="t('Events')"
            name="offering_types"
            :options="offeringTypeOptions"
          />
          <ScheduleFiltersAccordion
            v-if="ageRestrictionOptions?.length >= 1 && features.age_restrictions && showAgeFilter"
            :title="t('Ages')"
            name="age_restrictions"
            :options="ageRestrictionOptions"
          />
        </FormKit>
      </div>
    </div>
  </ScheduleFiltersLayout>
</template>

<i18n>
{
  "en-us": {
    "Filter": "Filter",
    "Venues": "Venues",
    "Categories": "Categories",
    "Instructors": "Instructors",
    "Event": "Event",
    "Fitness Disciplines": "Fitness Disciplines",
    "Rowing": "Rowing",
    "Running/Walking": "Running/Walking",
    "Strength/Cardio": "Strength/Cardio",
    "Clear Filters": "Clear Filters",
    "Events": "Events"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "Filter": "Filter",
  "Venues": "Veranstaltungsorte",
  "Categories": "Kategorien",
  "Instructors": "Trainer:in",
  "Event": "Veranstaltung",
  "Fitness Disciplines": "Fitness Disziplinen",
  "Rowing": "Rudern",
  "Running/Walking": "Laufen/Walking",
  "Strength/Cardio": "Kraft/Cardio",
  "Clear Filters": "Filter löschen",
  "Events": "Veranstaltungen"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "Filter": "Filtrer",
  "Venues": "Lieux",
  "Categories": "Catégories",
  "Instructors": "Instructeurs",
  "Event": "Événement",
  "Fitness Disciplines": "Disciplines de fitness",
  "Rowing": "Aviron",
  "Running/Walking": "Course/Marche",
  "Strength/Cardio": "Renforcement/Cardio",
  "Clear Filters": "Effacer les filtres",
  "Events": "Événements"
}
</i18n>

<i18n locale="es" lang="json">
{
  "Filter": "Filtrar",
  "Venues": "Sedes",
  "Categories": "Categorías",
  "Instructors": "Instructores",
  "Event": "Evento",
  "Fitness Disciplines": "Disciplinas de fitness",
  "Rowing": "Remo",
  "Running/Walking": "Carrera/Paseo",
  "Strength/Cardio": "Fuerza/Cardio",
  "Clear Filters": "Borrar Filtros",
  "Events": "Eventos"
}
</i18n>
